import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CrmApi } from '../../api/resources';
import { errorMessage } from '../../api/client';
import Can, { useCan } from '../../components/Can';
import ConfirmModal from '../../components/ConfirmModal';
import Icon from '../../components/Icon';
import LeadFormModal from './LeadFormModal';
import useCrmLookups, { StatusBadge, fmtDate } from './useCrmLookups';

const TABS = [
  ['all', 'All leads'],
  ['today', 'Today'],
  ['upcoming', 'Upcoming'],
  ['past', 'Past / overdue'],
];

/**
 * Follow-up windows by calendar day in the user's timezone, as ISO for the API:
 * past = before today, today = the whole of today, upcoming = after today.
 */
function followupRange(tab) {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 1);
  if (tab === 'today') return { next_from: start.toISOString(), next_to: end.toISOString() };
  if (tab === 'upcoming') return { next_from: end.toISOString() };
  if (tab === 'past') return { next_to: start.toISOString() };
  return {};
}

export default function LeadsPage() {
  const canUpdate = useCan('crm.leads.update');
  const canDelete = useCan('crm.leads.delete');
  const lookups = useCrmLookups();

  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusId, setStatusId] = useState('');
  const [tab, setTab] = useState('all'); // all | today | upcoming | past
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null); // { mode: 'create'|'edit'|'delete', lead }

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await CrmApi.leads.list({
        page,
        search: search || undefined,
        status_id: statusId || undefined,
        ...followupRange(tab),
      });
      setRows(data.data);
      setMeta(data.meta);
    } catch (err) {
      setError(errorMessage(err, 'Could not load leads.'));
    } finally {
      setLoading(false);
    }
  }, [page, search, statusId, tab]);

  useEffect(() => {
    load();
  }, [load]);

  const closeModal = () => setModal(null);
  const afterSave = () => {
    setModal(null);
    load();
  };

  const doDelete = async () => {
    const lead = modal.lead;
    try {
      await CrmApi.leads.remove(lead.id);
      setModal(null);
      load();
    } catch (err) {
      setModal(null);
      setError(errorMessage(err, 'Could not delete lead.'));
    }
  };

  const filter = (setter) => (e) => {
    setPage(1);
    setter(e.target.value);
  };

  return (
    <div>
      <div className="page-head">
        <div>
          <h1 className="page-title">Leads</h1>
          <p className="page-sub">Register leads, follow up and track their status.</p>
        </div>
        <Can permission="crm.leads.create">
          <button
            className="btn btn-primary"
            disabled={!lookups.ready}
            onClick={() => setModal({ mode: 'create' })}
          >
            <Icon name="plus" size={16} />
            Register lead
          </button>
        </Can>
      </div>

      {error && <div className="notice notice-error">{error}</div>}

      <div className="tabs">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            className={tab === key ? 'tab active' : 'tab'}
            onClick={() => {
              setPage(1);
              setTab(key);
            }}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="card">
        <div className="card-head">
          <div className="toolbar">
            <div className="search">
              <Icon name="search" />
              <input
                type="search"
                placeholder="Search name, phone, email…"
                value={search}
                onChange={filter(setSearch)}
              />
            </div>
            <select className="select-sm" value={statusId} onChange={filter(setStatusId)}>
              <option value="">All statuses</option>
              {lookups.statuses.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          {meta && <span className="muted">{meta.total ?? rows.length} total</span>}
        </div>

        <div className="table-wrap">
          <table className="grid">
            <thead>
              <tr>
                <th>Lead</th>
                <th>Status</th>
                <th>Assigned to</th>
                <th>Last follow-up</th>
                <th>Next follow-up</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading &&
                [0, 1, 2, 3].map((i) => (
                  <tr key={i}>
                    {[160, 70, 100, 110, 110, 60].map((w, j) => (
                      <td key={j}>
                        <span className="skeleton" style={{ width: w }} />
                      </td>
                    ))}
                  </tr>
                ))}

              {!loading &&
                rows.map((l) => (
                  <tr key={l.id}>
                    <td>
                      <Link to={`/crm/leads/${l.id}`} className="name">
                        {l.name}
                      </Link>
                      <div className="sub">{l.phone}</div>
                    </td>
                    <td>
                      <StatusBadge status={l.status} />
                    </td>
                    <td>{l.assignee?.name || <span className="muted">Unassigned</span>}</td>
                    <td>{fmtDate(l.last_followed_up_at)}</td>
                    <td>{fmtDate(l.next_follow_up_at)}</td>
                    <td>
                      <div className="row-actions">
                        {canUpdate && (
                          <button
                            className="icon-btn"
                            title="Edit"
                            aria-label={`Edit ${l.name}`}
                            onClick={() => setModal({ mode: 'edit', lead: l })}
                          >
                            <Icon name="edit" />
                          </button>
                        )}
                        {canDelete && (
                          <button
                            className="icon-btn danger"
                            title="Delete"
                            aria-label={`Delete ${l.name}`}
                            onClick={() => setModal({ mode: 'delete', lead: l })}
                          >
                            <Icon name="trash" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {!loading && rows.length === 0 && (
          <div className="empty">
            <div className="empty-icon">
              <Icon name="inbox" size={26} />
            </div>
            <strong>No leads found</strong>
            {tab === 'all' ? 'Register a lead to get started.' : 'No leads have a follow-up in this period.'}
          </div>
        )}

        {meta && meta.last_page > 1 && (
          <div className="pager">
            <span className="muted">
              Page {meta.current_page} of {meta.last_page}
            </span>
            <div className="toolbar">
              <button className="btn btn-sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Previous
              </button>
              <button
                className="btn btn-sm"
                disabled={page >= meta.last_page}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {modal?.mode === 'create' && (
        <LeadFormModal lookups={lookups} onClose={closeModal} onSaved={afterSave} />
      )}
      {modal?.mode === 'edit' && (
        <LeadFormModal lead={modal.lead} lookups={lookups} onClose={closeModal} onSaved={afterSave} />
      )}
      {modal?.mode === 'delete' && (
        <ConfirmModal
          title="Delete lead"
          message={`Delete ${modal.lead.name}? This cannot be undone.`}
          onConfirm={doDelete}
          onClose={closeModal}
        />
      )}
    </div>
  );
}
